import { clamp } from "lodash";

type OverlayPositionDirection = "top" | "right" | "bottom" | "left";

export interface PositionVector {
  top: number;
  left: number;
}

export interface GetOverlayPositionParams {
  /**
   * The preferred position for the overlay. Will automatically change if this is outside the viewport
   */
  position?: OverlayPositionDirection;
  /**
   * ClientRect for the target element the overlay position is based on
   */
  targetRect?: ClientRect;
  /**
   * ClientRect for the overlay element that will be positioned relative to the target
   */
  overlayRect?: ClientRect;
  /**
   * Distance between the target and overlay
   */
  offset?: number;
  /**
   * Size of the caret pointing to the target element
   */
  caretSize?: number;
  /**
   * Minimum distance between the overlay and the edges of the viewport
   */
  outerMargin?: number;
  /**
   * Stick to the viewport while scrolling
   */
  sticky?: boolean;
}

export interface OverlayPositionProps {
  /**
   * The top/left of the overlay element
   */
  overlay: PositionVector;
  /**
   * The top/left of the caret relative to the overlay
   */
  caret: PositionVector;
  /**
   * The name of the position to be used as a BEM modifier
   */
  modifier: OverlayPositionDirection;
}

/**
 * Gets a position based on a target rect and a preferred position, then clamps it to the viewport.
 * Useful for sharing overlay logic between components like popovers, tooltips, dropdowns, menus etc
 */
function getOverlayPosition({
  position = "bottom",
  targetRect,
  overlayRect,
  offset = 0,
  caretSize = 0,
  outerMargin = 0,
  sticky,
}: GetOverlayPositionParams): OverlayPositionProps {
  const { innerWidth, innerHeight } = window;

  if (!targetRect || !overlayRect) {
    return {
      overlay: { top: 0, left: 0 },
      caret: { top: 0, left: 0 },
      modifier: position,
    };
  }

  const overlayWidth = overlayRect.width;
  const overlayHeight = overlayRect.height;
  const offsetWithCaret = offset + caretSize;

  let modifier = position;

  // Preferred positions
  const overlays = {
    top: {
      top: targetRect.top - overlayHeight - offsetWithCaret,
      left: targetRect.left + targetRect.width / 2 - overlayWidth / 2,
    },
    right: {
      top: targetRect.top + targetRect.height / 2 - overlayHeight / 2,
      left: targetRect.right + offsetWithCaret,
    },
    bottom: {
      top: targetRect.bottom + offsetWithCaret,
      left: targetRect.left + targetRect.width / 2 - overlayWidth / 2,
    },
    left: {
      top: targetRect.top + targetRect.height / 2 - overlayHeight / 2,
      left: targetRect.left - overlayWidth - offsetWithCaret,
    },
  };

  const carets = {
    top: {
      top: overlayHeight,
      left: overlayWidth / 2 - caretSize,
    },
    right: {
      top: overlayHeight / 2 - caretSize,
      left: -caretSize,
    },
    bottom: {
      top: -caretSize,
      left: overlayWidth / 2 - caretSize,
    },
    left: {
      top: overlayHeight / 2 - caretSize,
      left: overlayWidth,
    },
  };

  // Set the preferred position
  const overlay = overlays[position];
  const caret = carets[position];

  const isTopOrBottom = position === "top" || position === "bottom";
  const isLeftOrRight = position === "left" || position === "right";

  // Clamp the preferred position if it goes outside the viewport, or flip its position

  // Clamp vertical
  if (overlay.top < outerMargin) {
    // Top
    if (isLeftOrRight) {
      const caretMin = sticky ? 0 : targetRect.height / 2 - caretSize;
      caret.top = clamp(overlay.top + overlayHeight / 2 - caretSize - outerMargin, caretMin, overlayHeight);
      overlay.top = sticky ? outerMargin : Math.min(outerMargin, targetRect.top);
    } else {
      overlay.top = sticky
        ? Math.max(outerMargin, Math.min(innerHeight - overlayHeight - offsetWithCaret, overlays.bottom.top))
        : overlays.bottom.top;
      caret.top = carets.bottom.top;
      modifier = "bottom";
    }
  } else if (overlay.top + overlayHeight > innerHeight - outerMargin) {
    // Bottom
    if (isLeftOrRight) {
      const overlap = overlay.top + overlayHeight - innerHeight;
      const caretMax = sticky
        ? overlayHeight - caretSize * 2
        : Math.min(overlayHeight - caretSize * 2, overlayHeight - targetRect.height / 2 - caretSize);

      caret.top = clamp(overlayHeight / 2 + overlap, 0, caretMax);
      overlay.top = sticky
        ? innerHeight - overlayHeight - outerMargin
        : Math.max(innerHeight - overlayHeight - outerMargin, targetRect.bottom - overlayHeight);
    } else {
      overlay.top = sticky
        ? Math.max(Math.min(innerHeight - overlayHeight - offsetWithCaret, overlays.top.top), outerMargin)
        : overlays.top.top;
      caret.top = carets.top.top;
      modifier = "top";
    }
  }

  // Clamp horizontal
  if (overlay.left + overlayWidth > innerWidth - outerMargin) {
    // Right
    if (isTopOrBottom) {
      const overlap = overlay.left + overlayWidth - innerWidth;
      caret.left = clamp(overlayWidth / 2 + overlap - caretSize + outerMargin, 0, overlayWidth);
      overlay.left = innerWidth - outerMargin - overlayWidth;
    } else {
      overlay.left = Math.max(outerMargin, overlays.left.left);
      caret.left = carets.left.left;
      modifier = "left";
    }
  } else if (overlay.left < outerMargin) {
    // Left
    if (isTopOrBottom) {
      caret.left = clamp(overlay.left + overlayWidth / 2 - caretSize - outerMargin, 0, overlayWidth);
      overlay.left = outerMargin;
    } else {
      overlay.left = Math.min(innerWidth - overlayWidth - offsetWithCaret, overlays.right.left);
      caret.left = carets.right.left;
      modifier = "right";
    }
  }

  return { modifier, overlay, caret };
}

export default getOverlayPosition;
